import React, { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import pointsService from '../../services/pointsService';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const Statistics = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const formatDate = (dateString) => {
    if (!dateString) return 'Unbekannt';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Ungültiges Datum';
    return date.toLocaleDateString('de-DE');
  };

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await pointsService.getStats();
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      setError('Fehler beim Laden der Statistiken');
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  const chartData = {
    labels: stats?.eventDetails?.map(event => event.name) || [],
    datasets: [
      {
        label: 'Buchungen',
        data: stats?.eventDetails?.map(event => event.bookings) || [],
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Buchungen pro Event',
      },
    },
  };

  return (
    <div>
      <h5>Statistiken</h5>

      {/* Overview Cards */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card bg-primary text-white">
            <div className="card-body text-center">
              <h4>{stats?.users?.totalUsers || 0}</h4>
              <p className="mb-0">Benutzer gesamt</p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-success text-white">
            <div className="card-body text-center">
              <h4>{stats?.events?.totalEvents || 0}</h4>
              <p className="mb-0">Events gesamt</p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-info text-white">
            <div className="card-body text-center">
              <h4>{stats?.users?.totalPointsInSystem || 0}</h4>
              <p className="mb-0">Punkte im System</p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-warning text-white">
            <div className="card-body text-center">
              <h4>{stats?.users?.averagePointsPerUser || 0}</h4>
              <p className="mb-0">Ø Punkte pro User</p>
            </div>
          </div>
        </div>
      </div>

      {/* Most Booked Event */}
      {stats?.mostBookedEvent && (
        <div className="card mb-4">
          <div className="card-header">
            <h6 className="mb-0">🏆 Meistgebuchtes Event</h6>
          </div>
          <div className="card-body">
            <h5>{stats.mostBookedEvent.name}</h5>
            <p className="mb-0">
              <strong>{stats.mostBookedEvent.bookings}</strong> von{' '}
              <strong>{stats.mostBookedEvent.maxParticipants}</strong> Plätzen gebucht
            </p>
          </div>
        </div>
      )}

      {/* Chart */}
      <div className="card mb-4">
        <div className="card-header">
          <h6 className="mb-0">📊 Buchungen pro Event</h6>
        </div>
        <div className="card-body">
          {stats?.eventDetails?.length > 0 ? (
            <Bar data={chartData} options={chartOptions} />
          ) : (
            <div className="alert alert-info">
              Keine Event-Daten für Chart verfügbar.
            </div>
          )}
        </div>
      </div>

      {/* Recent Activity */}
      {stats?.recentActivity && stats.recentActivity.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h6 className="mb-0">📈 Letzte Aktivitäten</h6>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Datum</th>
                    <th>Benutzer</th>
                    <th>Punkte</th>
                    <th>Grund</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recentActivity.map((activity, index) => (
                    <tr key={index}>
                      <td>
                        <small>
                          {formatDate(activity.date)}
                        </small>
                      </td>
                      <td>{activity.username}</td>
                      <td>
                        <span className={`badge ${activity.amount > 0 ? 'bg-success' : 'bg-danger'}`}>
                          {activity.amount > 0 ? '+' : ''}{activity.amount}
                        </span>
                      </td>
                      <td>
                        <small>{activity.reason}</small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Statistics;
